import React, { useMemo } from 'react';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { VendorPlan, PlanFeatures } from '../../../../modules/superadmin/our-vendors/models/vendor.model';
import { VendorInvoice } from '../../../../common/models/billing.model';
import { classifyPlan } from '../plan-cta.util';
import { useVendorPlan } from '../../../../common/hooks/use-vendor-plan.hook';

const FEATURE_LABELS: Partial<Record<keyof PlanFeatures, string>> = {
  apiIntegration: 'API Integration',
  emailAutomation: 'Email Automation',
  aiSpaceAnalysis: 'AI Space Analysis',
  seamlessGeneration: 'Seamless Generation',
  model3dGeneration: '3D Model Generation',
  advancedAnalytics: 'Advanced Analytics',
  contentStudio: 'Content Studio',
  socialScheduler: 'Social Scheduler',
};

const money = (amount?: number, currency?: string): string => {
  if (amount === undefined || amount === null) return '—';
  const symbol = (currency || 'usd').toLowerCase() === 'usd' ? '$' : `${(currency || '').toUpperCase()} `;
  return `${symbol}${amount.toLocaleString()}`;
};
const fmtDate = (iso?: string): string =>
  iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

interface AccountSummaryCardProps {
  currentPlan?: VendorPlan;
  invoices: VendorInvoice[] | null;
  hasActiveSubscription: boolean;
  subscriptionCurrentPeriodEnd?: string;
  /** Wire schedule from the vendor record — the next bill when no invoice exists yet. */
  wire?: { amount: number; interval: 'month' | 'year'; periodEnd: string };
  onUpgrade: () => void;
  onManage: () => void;
  onViewHistory: () => void;
  /** When true, drop the outer card chrome (border/bg) — used when merged into a shared card. */
  embedded?: boolean;
  /** No plan above this one: there is nothing to upgrade to, so no Upgrade button. */
  isTopPlan?: boolean;
}

// Micro-label above each summary block — the same eyebrow treatment everywhere.
const MicroLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mb-1.5 text-xs font-medium tracking-wider text-muted-foreground uppercase">{children}</p>
);

/**
 * "Account Type" summary card (styled after the reference billing page): the
 * current subscription + price + a primary CTA on the left, and Next/Last bill
 * details on the right. Bill data is derived from the Stripe subscription
 * (monthly card plans) or the most recent paid wire invoice.
 */
export const AccountSummaryCard: React.FC<AccountSummaryCardProps> = ({
  currentPlan,
  invoices,
  hasActiveSubscription,
  subscriptionCurrentPeriodEnd,
  wire,
  onUpgrade,
  onManage,
  onViewHistory,
  embedded = false,
  isTopPlan = false,
}) => {
  const { t } = useTranslation();
  // A sales-led agreement prices this vendor, not the plan (docs/pricing-v2.md).
  const contract = useVendorPlan()?.contract ?? null;

  const lastPaid = useMemo(() => {
    const paid = (invoices || []).filter((i) => i.status === 'paid');
    paid.sort((a, b) => new Date(b.paidAt || b.issuedAt).getTime() - new Date(a.paidAt || a.issuedAt).getTime());
    return paid[0];
  }, [invoices]);

  // Next bill: Stripe subscription renewal (monthly card) or the current wire period end.
  const nextBill = useMemo(() => {
    if (hasActiveSubscription && currentPlan?.price) {
      return { amount: currentPlan.price, currency: currentPlan.currency, date: subscriptionCurrentPeriodEnd };
    }
    // A vendor paying by transfer owes what their record says, from the day their paid period ends.
    if (wire) {
      return { amount: wire.amount, currency: currentPlan?.currency, date: wire.periodEnd };
    }
    if (lastPaid) {
      return { amount: lastPaid.amount, currency: lastPaid.currency, date: lastPaid.periodEnd };
    }
    return null;
  }, [hasActiveSubscription, currentPlan, subscriptionCurrentPeriodEnd, wire, lastPaid]);

  /*
   * By how the plan is sold, not by its stored price. A custom plan is quoted
   * per vendor and keeps `price: 0`, which used to read as "Free plan" — so an
   * Enterprise vendor saw "Enterprise · Free plan".
   */
  const tier = currentPlan ? classifyPlan(currentPlan) : 'free';
  const priceLabel = currentPlan?.pricingType === 'trial'
    ? t('PRICING.UPGRADE.TRIAL_DAYS', { count: currentPlan.trialDays || 14 })
    : tier === 'sales' && contract?.monthlyAmount
    ? `${money(contract.monthlyAmount, contract.currency)} / ${t('BILLING.PER_MONTH', 'month')} · ${t('PRICING.SUMMARY.TERM', { count: contract.termMonths })}`
    : tier === 'sales'
    ? t('BILLING.CUSTOM_PRICING', 'Custom pricing')
    : currentPlan?.price
      ? `${money(currentPlan.price, currentPlan.currency)} / ${t('BILLING.PER_MONTH', 'month')}`
      : t('BILLING.FREE_PLAN', 'Free plan');

  const enabledFeatures = currentPlan
    ? (Object.keys(FEATURE_LABELS) as (keyof PlanFeatures)[])
        .filter((k) => currentPlan.features?.[k])
        .slice(0, 4)
        .map((k) => FEATURE_LABELS[k] as string)
    : [];

  return (
    <div data-testid="account-summary" className={cn('p-6', !embedded && 'mb-6 rounded-xl border bg-card md:mb-8')}>
      <h2 className="font-heading mb-5 text-lg font-medium tracking-tight">
        {t('BILLING.ACCOUNT_TYPE', 'Account Type')}
      </h2>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-[1.4fr_1fr] md:gap-10">
        {/* Left: current subscription + CTA */}
        <div>
          <MicroLabel>{t('BILLING.YOUR_SUBSCRIPTION', 'Your subscription')}</MicroLabel>
          <div className="mb-4 flex flex-wrap items-baseline gap-2">
            <span className="font-heading text-xl font-medium tracking-tight">
              {currentPlan?.name || t('BILLING.FREE_PLAN', 'Free plan')}
            </span>
            <span className="text-sm text-muted-foreground">{priceLabel}</span>
          </div>

          {(hasActiveSubscription || !isTopPlan) && (
            <Button
              data-testid="account-summary-cta"
              variant="outline"
              onClick={hasActiveSubscription ? onManage : onUpgrade}
            >
              {hasActiveSubscription ? t('BILLING.MANAGE_SUBSCRIPTION', 'Manage subscription') : t('BILLING.UPGRADE_PLAN', 'Upgrade plan')}
            </Button>
          )}

          {enabledFeatures.length > 0 && (
            <div className="mt-5 flex flex-col gap-2">
              {enabledFeatures.map((label) => (
                <div key={label} className="flex items-center gap-2">
                  <Check className="size-4 shrink-0" />
                  <span className="text-sm text-muted-foreground">{label}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right: next + last bill */}
        <div>
          <div className="mb-5">
            <MicroLabel>{t('BILLING.YOUR_NEXT_BILL', 'Your next bill')}</MicroLabel>
            {nextBill ? (
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="font-heading text-xl font-medium tracking-tight">
                  {money(nextBill.amount, nextBill.currency)}
                </span>
                {nextBill.date && (
                  <span className="text-sm text-muted-foreground">
                    {t('BILLING.ON_DATE', 'on {{date}}', { date: fmtDate(nextBill.date) })}
                  </span>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{t('BILLING.NO_UPCOMING_BILL', 'No upcoming bill')}</p>
            )}
          </div>

          <div className="mb-4">
            <MicroLabel>{t('BILLING.YOUR_LAST_BILL', 'Your last bill')}</MicroLabel>
            {lastPaid ? (
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="font-heading text-xl font-medium tracking-tight">
                  {money(lastPaid.amount, lastPaid.currency)}
                </span>
                <span className="text-sm text-muted-foreground">
                  {t('BILLING.PAID_ON', 'paid {{date}}', { date: fmtDate(lastPaid.paidAt || lastPaid.issuedAt) })}
                </span>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{t('BILLING.NO_BILLS_YET', 'No bills yet')}</p>
            )}
          </div>

          <Button
            variant="link"
            onClick={onViewHistory}
            className="h-auto p-0 text-sm text-foreground underline"
          >
            {t('BILLING.VIEW_HISTORY', 'View billing history')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AccountSummaryCard;
